import { Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, IconButton, InputLabel, MenuItem, Select, Switch, TextField, Tooltip } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import setToast from "../../helpers/toaster.helper";
import * as selector from "../../selectors/selectors";
import { AddIcon, CancelIcon, DeleteChatIcon } from "../../icon";
import { CustomAttributeDefinitionModel, CustomAttributeFieldType, CustomAttributeOptionModel, fieldTypeToNumber, mapToCustomAttributeDefinition } from "../../models/custom-attribute.model";
import ButtonComponent from "../shared/button/button.component";
import { buttonLabels } from "../shared/constants";
import { ToasterType } from "../shared/toaster/toaster.model";
import { ContactService } from "../../services/ContactService";

export interface CustomAttributeDraft {
	name: string;
	fieldType: CustomAttributeFieldType | "";
	required: boolean;
	helpText: string;
	dropdownOptions: string[];
}

interface Props {
	show: boolean;
	onClose: (isConfirmed?: boolean) => void;
	onSave: (draft: CustomAttributeDraft) => void | Promise<void>;
	initialValue?: Partial<CustomAttributeDefinitionModel>;
	onAttributeSave?: (data: CustomAttributeDefinitionModel) => void;
}

const FIELD_TYPE_OPTIONS: { label: string, type: number, value: CustomAttributeFieldType }[] = [
	{ label: "Text", value: "TEXT", type: 0 },
	{ label: "Number", value: "NUMBER", type: 1 },
	{ label: "Date", value: "DATE", type: 2 },
	{ label: "Boolean", value: "BOOLEAN", type: 3 },
	{ label: "Drop Down", value: "DROPDOWN", type: 4 },
];

const MAX_LENGTH = 30;
const DROPDOWN_OPTION_MAX_LENGTH = 10;

const CustomAttributeDialog = (props: Props) => {
	const IsMobileDevice: boolean = useSelector(selector.isMobileDevice);
	const IsDarkMode: boolean = useSelector(selector.isDarkMode);
	const [title, setTitle] = useState("Add Custom Attribute");
	const [isLoading, setIsLoading] = useState(false);
	const [optionInput, setOptionInput] = useState("");

	const [form, setForm] = useState<CustomAttributeDraft>({
		name: "",
		fieldType: "",
		required: false,
		helpText: "",
		dropdownOptions: [],
	});

	const [errors, setErrors] = useState({
		name: "",
		fieldType: "",
		helpText: "",
		dropdownOptions: "",
	});

	useEffect(() => {
		const isEdit = !!props.initialValue?.Id;
		setTitle(isEdit ? "Edit Custom Attribute" : "Add Custom Attribute");
		setForm({
			name: props.initialValue?.Name ?? "",
			fieldType: FIELD_TYPE_OPTIONS.find((x) => x.type === props.initialValue?.Type)?.value ?? "",
			required: props.initialValue?.IsRequired ?? false,
			helpText: props.initialValue?.Description ?? "",
			dropdownOptions: props.initialValue?.DropdownOptions ?? [],
		});
		setOptionInput("");
		setErrors({ name: "", fieldType: "", helpText: "", dropdownOptions: "" });
	}, [props.show, props.initialValue]);

	const validateName = (value: string) => {
		if (!value.trim()) return "Attribute Name is required.";
		if (value.trim().length > MAX_LENGTH) return "Attribute Name should not exceed 30 characters.";
		return "";
	};

	const validateHelpText = (value: string) => {
		if (value.length > MAX_LENGTH) return "Help Text should not exceed 30 characters.";
		return "";
	};

	const handleNameChange = (value: string) => {
		setForm({ ...form, name: value });
		setErrors({ ...errors, name: validateName(value) });
	};

	const handleHelpTextChange = (value: string) => {
		setForm({ ...form, helpText: value });
		setErrors({ ...errors, helpText: validateHelpText(value) });
	};

	const handleFieldTypeChange = (value: CustomAttributeFieldType | "") => {
		setForm({ ...form, fieldType: value, dropdownOptions: value === "DROPDOWN" ? form.dropdownOptions : [] });
		setErrors({ ...errors, fieldType: "", dropdownOptions: "" });
	};

	const handleRequiredChange = (value: boolean) => {
		setForm({ ...form, required: value });
	};

	const addDropdownOption = () => {
		const label = optionInput.trim();
		if (!label) return;
		if (label.length > DROPDOWN_OPTION_MAX_LENGTH) return;

		setForm({ ...form, dropdownOptions: [...form.dropdownOptions, label] });
		setOptionInput("");
		setErrors({ ...errors, dropdownOptions: "" });
	};

	const removeDropdownOption = (index: number) => {
		setForm({ ...form, dropdownOptions: form.dropdownOptions.filter((_, i) => i !== index) });
	};

	const validateForm = () => {
		const newErrors = {
			name: validateName(form.name),
			fieldType: form.fieldType ? "" : "Field Type is required.",
			helpText: validateHelpText(form.helpText),
			dropdownOptions: "",
		};
		if (form.fieldType === "DROPDOWN" && form.dropdownOptions.length === 0) {
			newErrors.dropdownOptions = "Add at least one option for a drop down field.";
		}
		setErrors(newErrors);
		return !newErrors.name && !newErrors.fieldType && !newErrors.helpText && !newErrors.dropdownOptions;
	};

	const handleSave = async (e?: any) => {
		e?.preventDefault?.();
		if (!validateForm()) return;
		setIsLoading(true);

		// try {
		// 	await props.onSave({
		// 		...form,
		// 		name: form.name.trim(),
		// 		helpText: form.helpText.trim(),
		// 	});
		// 	setToast(props.initialValue?.Name ? "Custom attribute updated successfully." : "Custom attribute created successfully.", ToasterType.SUCCESS);
		// 	props.onClose(true);
		// } catch (err: any) {
		// 	const message = err?.response?.data?.message || err?.message || "Something went wrong.";
		// 	setToast(message, ToasterType.DANGER);
		// } finally {
		// 	setIsLoading(false);
		// }
		const formData = {
			...form,
			name: form.name.trim(),
			helpText: form.helpText.trim() ?? "",
		}
		const apiPayload = {
			Id: props.initialValue?.Id ?? "",
			Name: formData.name,
			Reference: buildReference(formData.name),
			IsDeleted: false,
			Type: fieldTypeToNumber(formData.fieldType as CustomAttributeFieldType),
			Options: buildOptionsForApi(formData, props.initialValue),
			Description: formData.helpText,
			IsRequired: formData.required,
		}

		if (props.initialValue) {
			ContactService.updateCustomAttribute(apiPayload).then((res) => {
				props.onClose(false);
				setIsLoading(false)
				setToast(props.initialValue?.Name ? "Custom attribute updated successfully." : "Custom attribute created successfully.", ToasterType.SUCCESS);
			}).catch((err) => {
				props.onClose(false);
				setIsLoading(false)
				const message = err?.response?.data?.message || err?.message || "Something went wrong.";
				setToast(message, ToasterType.DANGER);
			})

		} else {
			ContactService.createCustomAttribute(apiPayload).then((res) => {
				const data = mapToCustomAttributeDefinition(res.data)
				props.onAttributeSave(data)
				props.onClose(false);
				setToast(props.initialValue?.Name ? "Custom attribute updated successfully." : "Custom attribute created successfully.", ToasterType.SUCCESS);
				setIsLoading(false)
			}).catch((err) => {
				setIsLoading(false)
				props.onClose(false);
				const message = err?.response?.data?.message || err?.message || "Something went wrong.";
				setToast(message, ToasterType.DANGER);
			})
		}
	};

	const buildReference = (name: string): string => {
		return name
			.trim()
			.split(/\s+/)
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
			.join("");
	};

	const buildOptionsForApi = (draft: CustomAttributeDraft, editing: CustomAttributeDefinitionModel | null | Partial<CustomAttributeDefinitionModel>): CustomAttributeOptionModel[] => {
		const oldOptions: CustomAttributeOptionModel[] = (editing?.OptionsDetail ?? []).map((option) => ({
			Id: option.id,
			Label: option.label,
			IsDeleted: false,
		}));

		if (draft.fieldType !== "DROPDOWN") {
			return oldOptions.map((option) => ({
				Id: option.Id,
				Label: option.Label,
				IsDeleted: true,
			}));
		}

		const newLabels = (draft.dropdownOptions ?? [])
			.map((label) => label.trim())
			.filter(Boolean)
			.filter((label, index, arr) => arr.indexOf(label) === index);

		const activeOptions = newLabels.map((label) => {
			const oldMatch = oldOptions.find((option) => option.Label === label);
			return {
				Id: oldMatch?.Id ?? "",
				Label: label,
				IsDeleted: false,
			};
		});

		const deletedOptions = oldOptions
			.filter((option) => !newLabels.includes(option.Label))
			.map((option) => ({
				Id: option.Id,
				Label: option.Label,
				IsDeleted: true,
			}));

		return [...activeOptions, ...deletedOptions];
	};

	return (
		<Dialog
			maxWidth="sm"
			fullWidth={true}
			aria-labelledby="custom-attribute-dialog-title"
			open={props.show}
			onClose={() => props.onClose()}
			disableEscapeKeyDown
			classes={{ paper: `w-66 br-2 ${IsDarkMode ? "" : "dialogue-shadow"}` }}
			BackdropProps={{ style: { backgroundColor: "transparent" } }}
		>
			<DialogTitle
				id="custom-attribute-dialog-title"
				className={`p-2 ${IsDarkMode ? "bg-color-sidebar-bg" : ""}`}
			>
				<div className="d-flex">
					<span className={`h2-regular ${IsDarkMode ? "" : "color-text-field-font"}`}>
						{title}
					</span>
					<Tooltip title="Close" placement="bottom">
						<CancelIcon className={"c-pointer ml-auto"} onClick={() => props.onClose()} />
					</Tooltip>
				</div>
			</DialogTitle>

			<div className={"bb-primary-background-pink-4 mx-2"}></div>

			<DialogContent className={`pt-2 px-2 pb-0 o-x-hidden ${IsDarkMode ? "bg-color-sidebar-bg" : ""}`}>
				<form noValidate autoComplete="off" onSubmit={handleSave} className="m-auto">
					<Grid container direction="row" spacing={2}>
						<Grid item xs={12}>
							<TextField
								autoFocus
								label="Attribute Name"
								placeholder="Name"
								variant="outlined"
								size="small"
								required
								fullWidth
								value={form.name}
								onChange={(e) => handleNameChange(e.target.value)}
								InputProps={{ classes: { input: "h4-regular color-text-field-font" } }}
							/>
							<FormHelperText className="h6-regular color-primary-danger t-align-right">
								{errors.name}
							</FormHelperText>
						</Grid>

						<Grid item xs={12}>
							<FormControl fullWidth size="small" required>
								<InputLabel
									id="field-type-desktop-label"
									className={"h4-regular color-text-field-font"}
								>
									Field Type
								</InputLabel>
								<Select
									labelId="field-type-desktop-label"
									label="Field Type"
									value={form.fieldType}
									onChange={(e) => handleFieldTypeChange(e.target.value as CustomAttributeFieldType)}
									className={"h4-regular color-text-field-font"}
									inputProps={{ className: "h4-regular color-text-field-font" }}
									MenuProps={{
										PaperProps: {
											className: IsDarkMode ? "bg-color-sidebar-bg" : undefined,
										},
									}}
								>
									{FIELD_TYPE_OPTIONS.map((opt) => (
										<MenuItem key={opt.value} value={opt.value}>
											{opt.label}
										</MenuItem>
									))}
								</Select>
								<FormHelperText className="h6-regular color-primary-danger t-align-right">
									{errors.fieldType}
								</FormHelperText>
							</FormControl>
						</Grid>

						{form.fieldType === "DROPDOWN" && (
							<Grid item xs={12}>
								<div className={`p-2 br-2 ${IsDarkMode ? "bg-color-primary-background" : "bg-color-grid"}`}>
									<div className="d-flex align-center mb-2">
										<span className="h4-medium color-text-field-font">Options Management Section</span>
									</div>

									<p className="h6-regular color-nav-bar-font-color text-uppercase mb-2">
										Add and save options
									</p>

									<div className="d-flex flex-direction gap-2">
										{form.dropdownOptions.map((opt, idx) => (
											<div
												key={`${opt}-${idx}`}
												className="d-flex align-center justify-between px-2 py-1 br-2 mb-1 bg-color-grid-options-menu border-primary-divider"
											>
												<span className="h4-regular color-text-field-font">{opt}</span>
												<Tooltip title="Remove" placement="top">
													<span className="c-pointer">
														<DeleteChatIcon className="c-pointer" onClick={() => removeDropdownOption(idx)} />
													</span>
												</Tooltip>
											</div>
										))}
									</div>

									<div className="d-flex align-center mt-2 gap-1">
										<TextField
											size="small"
											fullWidth
											placeholder="Enter option label"
											variant="outlined"
											value={optionInput}
											onChange={(e) =>
												setOptionInput(
													e.target.value.slice(0, DROPDOWN_OPTION_MAX_LENGTH)
												)
											}
											onKeyDown={(e) => {
												if (e.key === "Enter") {
													e.preventDefault();
													addDropdownOption();
												}
											}}
											InputProps={{ classes: { input: "h4-regular color-text-field-font" } }}
										/>
										<IconButton
											color="primary"
											size="small"
											className="br-1"
											onClick={addDropdownOption}
											aria-label="add option"
										>
											<AddIcon />
										</IconButton>
									</div>

									<FormHelperText className="h6-regular color-primary-danger t-align-right">
										{errors.dropdownOptions}
									</FormHelperText>
								</div>
							</Grid>
						)}

						<Grid item xs={12}>
							<div className={`${IsMobileDevice ? "" : IsDarkMode ? "RichEditor-root-border br-2" : "schedule-report-border br-2"}`}>
								<Grid container>
									<Grid item xs={IsMobileDevice ? 9 : 10} className="pl-2 t-align-left d-flex align-center">
										<p className={`${IsMobileDevice ? "h2" : "h4-medium"} color-text-field-font t-align-left`}>
											<span>Required Field</span>
											<div className="h6-regular color-nav-bar-font-color">
												Makes this field mandatory for all contacts
											</div>
										</p>
									</Grid>
									<Grid item className="b-none d-flex my-auto justify-center" xs={IsMobileDevice ? 3 : 2}>
										<span className="ml-auto mr-3">
											<Switch
												checked={form.required}
												onChange={(e) => handleRequiredChange(e.target.checked)}
											/>
										</span>
									</Grid>
								</Grid>
							</div>
						</Grid>

						<Grid item xs={12}>
							<TextField
								label="Help Text (Optional)"
								placeholder="Add a short description for this field"
								variant="outlined"
								size="small"
								fullWidth
								multiline
								minRows={2}
								value={form.helpText}
								onChange={(e) => handleHelpTextChange(e.target.value)}
								InputProps={{ classes: { input: "h4-regular color-text-field-font" } }}
							/>
							<FormHelperText className="h6-regular color-primary-danger t-align-right">
								{errors.helpText}
							</FormHelperText>
						</Grid>
					</Grid>
				</form>
			</DialogContent>

			<DialogActions className={`pb-2 px-2 ${IsDarkMode ? " bg-color-sidebar-bg" : ""}`}>
				<ButtonComponent
					OnClick={() => props.onClose()}
					ButtonLabel={buttonLabels.CANCEL}
					Variant={"secondary-btn-small"}
				/>
				<ButtonComponent
					OnClick={handleSave}
					ButtonLabel={"Save"}
					Loading={isLoading}
					ClassName={"px-4"}
					Variant={"primary-btn-small"}
				/>
			</DialogActions>
		</Dialog>
	);
};

export default CustomAttributeDialog;